import { Text } from '@wfp/react';
import sortBy from 'lodash/sortBy';
import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { resolveCrf } from 'src/crf';
import { SECTION_DETAIL_DATA_KEY } from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import type { IComment, ICommentPermissions } from 'src/types';
import CommentListItem from './CommentListItem';

interface Props {
  user?: string;
  permissions?: ICommentPermissions;
  sectionAPIKwargs?: any;
  commentsContainerID?: string;
  comments?: IComment[];
  selectedFilter?: string;
  expandAll?: boolean;
}

export const CommentList = ({
  user,
  permissions,
  sectionAPIKwargs,
  commentsContainerID,
  comments,
  selectedFilter = 'all',
  expandAll = true,
}: Props) => {
  const viewData = useSelector((state: RootState) => state.viewData);
  const crf = resolveCrf(viewData.ACR_REPORTS_DETAIL?.data?.crf);

  const multiNarratives = viewData.ACR_SECTIONS_DETAIL?.data?.narratives;
  const narrativeContent = useSelector(
    (state: RootState) =>
      state.viewData[SECTION_DETAIL_DATA_KEY]?.data?.content,
  );
  const highlightComment = useSelector(
    (state: RootState) => state.viewState?.highlightComment,
  );

  const scrollSharedOptions = {
    duration: 800,
    delay: 0,
    smooth: 'easeInOutQuart',
    ignoreCancelEvents: true,
  };

  const dispatch = useDispatch();

  function removeHighlight() {
    dispatch(
      changeViewStateAction({
        highlightComment: undefined,
      }),
    );
  }

  function filterComments(data: any[]) {
    if (selectedFilter === 'all') {
      return data;
    }
    if (selectedFilter === 'opened') {
      return data.filter(comment => !comment.closed);
    }
    if (selectedFilter === 'important') {
      return data.filter(comment => comment.important);
    }
    if (selectedFilter === 'owner') {
      return data.filter(comment => comment.user.username === user);
    }
    return data;
  }

  function getAnchorsList(content?: string) {
    if (!content) return [];

    let narrativeArray = content.split('id="');
    narrativeArray.shift();
    narrativeArray = narrativeArray.map(item => item.split('" name', 1)[0]);
    return narrativeArray;
  }

  function getSortedLinkedComments(
    linkedComments: any[] = [],
    anchorsList: string[] = [],
  ) {
    return linkedComments.sort(
      (a, b) => anchorsList.indexOf(a.anchor) - anchorsList.indexOf(b.anchor),
    );
  }

  const orderedComments = useMemo(() => {
    const filteredComments = filterComments(comments);

    let unlinkedComments: any[] = [];
    let linkedComments: any[] = [];

    if (crf.commentOrdering === 'linked-unlinked') {
      const narrativeArray = getAnchorsList(narrativeContent);
      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          linkedComments.push(comment);
        } else {
          unlinkedComments.push(comment);
        }
      });

      linkedComments = getSortedLinkedComments(linkedComments, narrativeArray);

      return [...linkedComments, ...unlinkedComments];
    }

    const multiNarrativesOrdered = sortBy(multiNarratives, 'order');
    multiNarrativesOrdered.forEach(narrative => {
      let multiNarrativeLinkedComments: any[] = [];
      const narrativeArray = getAnchorsList(narrative.content);

      filteredComments.forEach(comment => {
        if (
          comment.anchor !== null &&
          narrativeArray.includes(comment.anchor)
        ) {
          multiNarrativeLinkedComments.push({
            ...comment,
            narrativeLinkedTitle: narrative.title,
          });
        }
      });

      multiNarrativeLinkedComments = getSortedLinkedComments(
        multiNarrativeLinkedComments,
        narrativeArray,
      );

      linkedComments.push(...multiNarrativeLinkedComments);
    });

    unlinkedComments = filteredComments
      .filter(
        item => linkedComments.findIndex(comm => comm.pk === item.pk) === -1,
      )
      .map(comment => ({
        ...comment,
        narrativeLinkedTitle: 'Untagged comments',
      }));

    return [...linkedComments, ...unlinkedComments];
  }, [narrativeContent, comments, selectedFilter]);

  useEffect(() => {
    if (!highlightComment) return;

    let timer: ReturnType<typeof setTimeout>;

    const findCommentOrReply = (comments: any[], targetId: string) => {
      for (const comment of comments) {
        if (comment.pk.toString() === targetId) {
          return {
            comment,
            isReply: false,
            scrollId: comment.anchor || `comment-${comment.pk}`,
          };
        }

        if (comment.comments && comment.comments.length > 0) {
          for (const reply of comment.comments) {
            if (reply.pk.toString() === targetId) {
              return {
                comment: reply,
                isReply: true,
                scrollId: `comment-${reply.pk}`,
              };
            }
          }
        }
      }
      return null;
    };

    const findByAnchor = (comments: any[], targetAnchor: string) => {
      for (const comment of comments) {
        if (comment.anchor === targetAnchor) {
          return {
            comment,
            isReply: false,
            scrollId: targetAnchor,
          };
        }
      }
      return null;
    };

    let foundItem = findCommentOrReply(orderedComments, highlightComment);

    if (!foundItem) {
      foundItem = findByAnchor(orderedComments, highlightComment);
    }

    if (foundItem) {
      timer = setTimeout(() => {
        removeHighlight();
      }, 5000);

      const isReplyInClosedComment =
        foundItem.isReply &&
        orderedComments.find(
          comment =>
            comment.comments?.some(
              reply => reply.pk.toString() === highlightComment,
            ) && comment.closed,
        );
      const scrollDelay = isReplyInClosedComment ? 200 : 0;

      setTimeout(() => {
        if (document.documentElement.scrollTop === 0) {
          scroller.scrollTo('section-wrapper-main', scrollSharedOptions);
        }

        scroller.scrollTo(foundItem.scrollId, {
          ...scrollSharedOptions,
          containerId: 'acr-comments-wrapper',
          offset: -50,
        });
      }, scrollDelay);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [highlightComment, orderedComments, comments]);

  const shouldHighlight = (comment: any) => {
    return (
      comment.anchor === highlightComment ||
      comment.pk.toString() === highlightComment
    );
  };

  return (
    <>
      {orderedComments?.map((comment, index) => (
        <Fragment key={comment.pk}>
          {comment.narrativeLinkedTitle &&
            (index === 0 ||
              orderedComments[index - 1].narrativeLinkedTitle !==
                comment.narrativeLinkedTitle) && (
              <div
                className={`comment-item-linked-narrative-title${
                  index === 0 ? ' first-narrative-title' : ''
                }`}
              >
                <Text kind="h6">{comment.narrativeLinkedTitle}</Text>
              </div>
            )}
          <CommentListItem
            activeUser={user}
            permissions={permissions}
            sectionAPIKwargs={sectionAPIKwargs}
            commentsContainerID={commentsContainerID}
            id={comment.pk}
            data={comment}
            highlightIsHovered={shouldHighlight(comment)}
            highlightComment={highlightComment}
            expandAll={expandAll}
          />
        </Fragment>
      ))}
    </>
  );
};

export default CommentList;
